/*
 * AdminShell — the admin's own chrome: 17px brand rule, white header,
 * sidebar, grey canvas holding one raised white sheet.
 */

import React from 'react'
import { NavLink } from 'react-router-dom'
import logo from '../../assets/Liputon-orange.svg'

const NAV = [
  { items: [{ to: '/admin/statistiikka', label: 'Statistiikka', icon: 'chart' }] },
  {
    group: 'Myynti',
    items: [
      { to: '/admin/raportit', label: 'Raportit', icon: 'report' },
      { to: '/admin/sepa', label: 'SEPA ja tilitykset', icon: 'sepa' },
      { to: '/admin/tyokalut', label: 'Työkalut', icon: 'tools' }
    ]
  },
  {
    group: 'Hallinta',
    items: [
      { to: '/admin/liput', label: 'Liput', icon: 'ticket' },
      { to: '/admin/tilaukset', label: 'Tilaukset', icon: 'order' },
      { to: '/admin/asiakkaat', label: 'Asiakasrekisteri', icon: 'users' }
    ]
  },
  {
    group: 'Järjestelmä',
    items: [
      { to: '/admin/kaannokset', label: 'Käännökset', icon: 'lang' },
      { to: '/admin/sahkopostivahdit', label: 'Sähköpostivahdit', icon: 'bell' }
    ]
  }
]

const PATHS = {
  chart: ['M3 3v18h18', 'M18 17V9', 'M13 17V5', 'M8 17v-3'],
  report: [
    'M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z',
    'M14 2v5h6',
    'M16 13H8',
    'M16 17H8'
  ],
  sepa: [
    'M2 8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z',
    'M6 12h.01',
    'M18 12h.01'
  ],
  tools: [
    'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94z'
  ],
  ticket: [
    'M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z',
    'M13 5v2',
    'M13 11v2',
    'M13 17v2'
  ],
  order: [
    'M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12'
  ],
  users: [
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2',
    'M22 21v-2a4 4 0 0 0-3-3.87',
    'M16 3.13a4 4 0 0 1 0 7.75'
  ],
  lang: [
    'm5 8 6 6',
    'm4 14 6-6 2-3',
    'M2 5h12',
    'M7 2h1',
    'm22 22-5-10-5 10',
    'M14 18h6'
  ],
  bell: [
    'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9',
    'M10.3 21a1.94 1.94 0 0 0 3.4 0'
  ],
  logout: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'm16 17 5-5-5-5', 'M21 12H9'],
  user: ['M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2']
}

const Icon = ({ name, size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {(PATHS[name] || []).map((d, i) => (
      <path key={i} d={d} />
    ))}
    {name === 'users' && <circle cx="9" cy="7" r="4" />}
    {name === 'user' && <circle cx="12" cy="7" r="4" />}
    {name === 'order' && (
      <React.Fragment>
        <circle cx="8" cy="21" r="1" />
        <circle cx="19" cy="21" r="1" />
      </React.Fragment>
    )}
    {name === 'sepa' && <circle cx="12" cy="12" r="2" />}
  </svg>
)

const AdminShell = ({
  title,
  subtitle,
  actions,
  badges = {},
  userName,
  onLogout,
  children
}) => (
  <div className="admin-v2">
    <div className="admin-v2__rule" />

    <header className="admin-v2__header">
      <div className="admin-v2__brand">
        <img className="admin-v2__logo" src={logo} alt="Liputon" />
        <span className="admin-v2__wordmark">Hallintapaneeli</span>
      </div>
      <div className="admin-v2__header-actions">
        <span className="u-row" style={{ gap: 8 }}>
          <Icon name="user" />
          {userName || 'Admin'}
        </span>
        <span className="admin-v2__divider" />
        <button type="button" className="btn btn--ghost" onClick={onLogout}>
          Kirjaudu ulos <Icon name="logout" />
        </button>
      </div>
    </header>

    <div className="admin-v2__body">
      <nav className="admin-v2__sidebar" aria-label="Hallintapaneelin navigaatio">
        {NAV.map((section, si) => (
          <React.Fragment key={si}>
            {section.group && (
              <div className="admin-v2__navgroup">{section.group}</div>
            )}
            {section.items.map(item => {
              const key = item.to.split('/').pop()
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className="admin-v2__navitem"
                  activeClassName="is-active"
                >
                  <Icon name={item.icon} />
                  <span>{item.label}</span>
                  {badges[key] ? (
                    <span className="admin-v2__navbadge">{badges[key]}</span>
                  ) : null}
                </NavLink>
              )
            })}
          </React.Fragment>
        ))}
      </nav>

      <main className="admin-v2__main">
        <div className="admin-v2__sheet">
          {(title || actions) && (
            <div className="admin-v2__pagehead">
              <div>
                {title && <h1>{title}</h1>}
                {subtitle && <p className="admin-v2__subtitle">{subtitle}</p>}
              </div>
              {actions}
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  </div>
)

export default AdminShell
